import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Radar, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { GoogleSignInButton } from "@/components/GoogleSignInButton";
import { guestWL } from "@/lib/guestWatchlists";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    const hadGuestLists = guestWL.hasData();
    try {
      await register(email, password, name);
      if (hadGuestLists) toast.success("Welcome! Your device watchlists moved into your account.");
      navigate("/feed");
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#080B11] sq-grid-bg flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-[#0F1420] border border-[#232D42] rounded-2xl p-8 shadow-2xl sq-fade-up">
        <Link to="/" className="flex items-center gap-2 justify-center mb-6">
          <div className="h-9 w-9 rounded-lg bg-[#00E5A3] flex items-center justify-center"><Radar size={20} className="text-[#04140F]" /></div>
          <span className="font-heading font-extrabold text-xl text-slate-100">Sideline<span className="text-[#00E5A3]">IQ</span></span>
        </Link>
        <h1 className="font-heading text-2xl font-bold text-slate-100 text-center">Create your account</h1>
        <p className="text-sm text-slate-400 text-center mt-1 mb-4">Free forever · your device watchlists transfer automatically</p>

        <div className="flex items-start gap-2 rounded-lg bg-[#151B2B] border border-[#232D42] px-3 py-2.5 mb-6">
          <ShieldCheck size={16} className="text-[#00E5A3] mt-0.5 shrink-0" />
          <p className="text-xs text-slate-400 leading-relaxed">
            We keep it lean and secure — email &amp; password only, no card required for free. Anything you saved as a guest comes with you.
          </p>
        </div>

        {error && <div data-testid="register-error" className="mb-4 rounded-lg bg-[#FF4D4D]/10 border border-[#FF4D4D]/30 px-3 py-2 text-sm text-[#FF7A7A]">{error}</div>}

        <GoogleSignInButton label="Sign up with Google" />
        <div className="flex items-center gap-3 my-5">
          <div className="h-px flex-1 bg-[#232D42]" />
          <span className="text-xs text-slate-600 font-mono">or</span>
          <div className="h-px flex-1 bg-[#232D42]" />
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-slate-300">Name</Label>
            <Input data-testid="register-name-input" value={name} onChange={(e) => setName(e.target.value)}
              className="mt-1 bg-[#151B2B] border-[#232D42] text-slate-100" required />
          </div>
          <div>
            <Label className="text-slate-300">Email</Label>
            <Input data-testid="register-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              className="mt-1 bg-[#151B2B] border-[#232D42] text-slate-100" required />
          </div>
          <div>
            <Label className="text-slate-300">Password</Label>
            <Input data-testid="register-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              className="mt-1 bg-[#151B2B] border-[#232D42] text-slate-100" placeholder="Min 6 characters" required />
          </div>
          <Button data-testid="register-submit-btn" type="submit" disabled={loading}
            className="w-full bg-[#00E5A3] hover:bg-[#00C98E] text-[#04140F] font-semibold h-11">
            {loading ? "Creating…" : "Create Account"}
          </Button>
        </form>
        <p className="text-sm text-slate-400 text-center mt-5">
          Already have an account? <Link data-testid="go-login-link" to="/login" className="text-[#00E5A3] hover:underline">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
